import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import { getValidFileTypes } from "./validFiles.config";
import { OSSIcon } from "@oss/components";

const useStyles = makeStyles(theme => ({
  uploadArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "180px",
    border: `1px dashed ${theme.palette.grey[500]}`,
    position: "relative",
    overflow: "hidden",
    padding: theme.spacing(2)
  },
  gutterBottomClass: {
    marginBottom: theme.spacing(2)
  },
  fileInput: {
    display: "none"
  },
  uploadAreaDragOver: {
    background: theme.palette.secondary.light
  },
  uploadAreaHasFile: {
    background: theme.palette.grey[200],
    border: `1px solid ${theme.palette.links}`
  },
  cloudUploadStyles: {
    fontSize: theme.typography.h3.fontSize
  },
  uploadWarning: {
    border: `1px solid ${theme.palette.error}`
  },
  clearUploadZoneButtonStyles: {
    zIndex: theme.zIndex.modal,
    position: "absolute",
    top: 0,
    right: 0
  },
  instructions: {
    fontFamily: theme.typography?.brand?.fontFamily,
    fontSize: 16
  }
}));

/**
 *
 * @param {boolean} disabled - Will block uploading if true
 * @param {array} file - Don't pass file[0]
 * @param {function} setFiles - however you would use the state setting of this file upload
 * @param {string} validExtensions - .csv,.zip,.txt Comma delimited, dot noted extensions
 * @param {string} uploadAreaClass - totally optional styles
 * @param {string} dropzoneTitle - If passed, will render the string right above the clouddownload icon
 * @param {object} dropzoneTitleProps - All props for mui Typography are valid here
 */
const OSSFileUpload = ({
  disabled = false,
  file = [],
  setFiles = () => {},
  validExtensions = "",
  uploadAreaClass = "",
  dropzoneTitle = null,
  dropzoneTitleProps = {},
  onTestUpload = null,
  gutterBottom = true
}) => {
  const {
    uploadArea,
    gutterBottomClass,
    fileInput,
    uploadAreaDragOver,
    uploadAreaHasFile,
    uploadWarning,
    clearUploadZoneButtonStyles,
    cloudUploadStyles,
    instructions
  } = useStyles();
  const [filename, setFilename] = useState("");
  const [highlight, setHighlight] = useState(false);
  const [invalidFileDropped, setInvalidFileDropped] = useState(false);
  const fileInputRef = useRef();

  const uploadAreaClasses = [
    uploadArea,
    uploadAreaClass,
    gutterBottom ? gutterBottomClass : "",
    highlight ? uploadAreaDragOver : "",
    filename ? uploadAreaHasFile : "",
    invalidFileDropped ? uploadWarning : ""
  ].join(" ");

  const openFileDialog = () => {
    fileInputRef.current.value = null;
    fileInputRef.current.click();
  };

  const clearUploadZone = (evt = null) => {
    if (evt) evt.stopPropagation();
    setFilename(null);
    setHighlight(false);
    setFiles([]);
  };

  const fileIsValidType = ({ name, type }) => {
    // Remove the extension from the back of the file
    const extension = name.split(".").pop().toLowerCase();
    // Confirm that the file uploaded matches the string passed in
    const fileExtensionIsValid = validExtensions.includes(`.${extension}`);
    // Right of the bat, if this doesn't work kill the processes
    if (!fileExtensionIsValid) {
      return false;
    }
    // If the extension is correct, we want to run a check against the extensions to see if the file matches the mimetypes
    const validFileTypesBasedOnExtension = getValidFileTypes(extension);
    const fileTypeIsValid = validFileTypesBasedOnExtension.includes(
      type.toLowerCase(extension)
    );
    return fileExtensionIsValid && fileTypeIsValid;
  };

  const setFilenameAndExtension = files => {
    const isValidType = fileIsValidType(files[0]);
    if (!isValidType) {
      // Remove any files
      clearUploadZone();
      setInvalidFileDropped(true);
    } else {
      setInvalidFileDropped(false);
      setFilename(files[0].name);
      setFiles(files);
    }
  };

  const onFilesAdded = evt => {
    // When a file is added through manual browsing
    // This method runs the validation for the uploaded file
    evt.preventDefault();
    if (disabled || filename) return;
    const files = evt.target.files;
    onTestUpload && onTestUpload(files);
    setFilenameAndExtension(files);
  };

  const onDrop = evt => {
    // Presently only serves its purpose on top of the container as a whole.
    // onDrop is baked into React so it catches this on top of the Grid wrapper
    // evt.dataTransfer returns undefined if used in `onFilesAdded`
    evt.preventDefault();
    if (filename) return;
    const files = evt.dataTransfer.files;
    onTestUpload && onTestUpload(files);
    setFilenameAndExtension(files);
  };

  const onDragOver = evt => {
    // This method updates the styles of the drop zone if the user is dragging over the top
    evt.preventDefault();
    if (filename) return;
    setHighlight(true);
  };

  useEffect(() => {
    if (file.length > 0) {
      setFilenameAndExtension(file);
    } else if (file.length === 0 && filename) {
      clearUploadZone();
    }
  }, [file]);

  const renderValidExtensionsText = () => {
    // If multiple valid extensions are passed,
    // We want to split at the comma and add an or string (could get messy for long ones)
    const split = validExtensions.split(",");
    // This works automatically with one passed because there is no comma
    // So the join never actually occurs
    const joined = split.join(" or ");
    return joined;
  };

  return (
    <Grid
      container
      className={uploadAreaClasses}
      onDragOver={onDragOver}
      onDragLeave={() => setHighlight(false)}
      onDrop={onDrop}
    >
      <input
        ref={fileInputRef}
        accept={validExtensions}
        type="file"
        className={fileInput}
        onInput={onFilesAdded}
        data-testid="file-upload-input"
      />
      {filename ? (
        <>
          <IconButton
            className={clearUploadZoneButtonStyles}
            onClick={clearUploadZone}
            aria-label="clear file"
            title="clear file"
          >
            <CloseIcon />
          </IconButton>
          <Grid container justifyContent="center">
            <OSSIcon name="fileExtension" filename={filename} />
          </Grid>
        </>
      ) : (
        <>
          {dropzoneTitle && (
            <Typography align="center" {...dropzoneTitleProps}>
              {dropzoneTitle}
            </Typography>
          )}
          <IconButton
            onClick={openFileDialog}
            aria-label="upload"
            title="upload"
            data-testid="cloud-upload-icon"
          >
            <CloudUploadIcon className={cloudUploadStyles} />
          </IconButton>
          {invalidFileDropped ? (
            <Typography
              color="error"
              data-testid="error-text"
              className={instructions}
            >
              It looks like the file you tried to upload is the wrong format!
              <br /> Try again with a {renderValidExtensionsText()}.
            </Typography>
          ) : (
            <Typography align="center" className={instructions}>
              Drag and drop
              <br /> a {renderValidExtensionsText()} file, or click to{" "}
              <span
                onClick={openFileDialog}
                data-testid="browse-link"
                style={{
                  cursor: "pointer"
                }}
              >
                browse
              </span>
              .
            </Typography>
          )}
        </>
      )}
    </Grid>
  );
};

OSSFileUpload.propTypes = {
  disabled: PropTypes.bool,
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  setFiles: PropTypes.func.isRequired,
  validExtensions: PropTypes.string.isRequired,
  uploadAreaClass: PropTypes.string,
  dropzoneTitle: PropTypes.bool,
  dropzoneTitleProps: PropTypes.object,
  onTestUpload: PropTypes.func
};

export default OSSFileUpload;
